import { Box, Grid, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import { useSnackbar } from "notistack";
import { getAllPurchaseOrdersExportReport } from "../../../features/Purchase/PurchaseOrder/purchase-order-actions";
import { useForm } from "react-hook-form";
import moment from "moment";
import { dateToUnix } from "../../../utils/date-converter";
import { CustomizedBox } from "../../../components/Custom/CustomizedBox";
import NewReportDateFilter from "../../../components/UI/NewReportDateFilter";
import { exportCSVParams } from "../../../utils/filterparams";
import PurchaseOrderTable from "../../../components/Table/DocumentTable/PurchaseOrderTable";
import { purchaseOrderActions } from "../../../features/Purchase/PurchaseOrder/purchase-order-slice";

const PurchaseOrderReport = () => {
  const { allPurchaseOrdersExport, isLoading } = useSelector(
    (state) => state.purchaseOrder
  );
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const gridRef = useRef();

  const breadcrumbs = [
    {
      name: t("purchase.index"),
      to: "/purchase",
    },
    {
      name: t("purchase.report.index"),
      to: "/purchase/report",
    },
    {
      name: "รายการ" + t("purchase.order.index"),
    },
  ];

  const { control, getValues, reset, setValue } = useForm({
    defaultValues: {
      dateType: "lastWeek",
      date: new Date(moment().startOf("day").subtract(6, "day")),
      dateTo: new Date(moment().endOf("day")),
    },
  });

  const onBtnExport = useCallback(() => {
    let startDate = getValues("date");
    let endDate = getValues("dateTo");

    const params = exportCSVParams(
      t("purchase.order.index"),
      startDate,
      endDate
    );
    gridRef.current.api.exportDataAsCsv(params);
    enqueueSnackbar("นำออกใบสั่งซื้อสำเร็จ", {
      variant: "success",
    });
  }, [enqueueSnackbar, getValues, t]);

  const onFilterReset = () => {
    if (gridRef) {
      gridRef.current.api.setFilterModel({});
    }
    reset({
      dateType: "lastWeek",
      date: new Date(moment().startOf("day").subtract(6, "day")),
      dateTo: new Date(moment().endOf("day")),
    });
    dispatch(
      getAllPurchaseOrdersExportReport(
        {
          startRow: 0,
          endRow: Math.pow(10, 10),
          filterModel: {
            issue_date: {
              filter: parseInt(
                dateToUnix(new Date(moment().startOf("day").subtract(6, "day")))
              ),
              filterTo: parseInt(dateToUnix(new Date(moment().endOf("day")))),
              filterType: "number",
              type: "inRange",
            },
          },
          sortModel: [],
        },
        enqueueSnackbar
      )
    );
  };

  const getFilteredPurchaseOrder = useCallback(() => {
    let startDate = getValues("date");
    let endDate = getValues("dateTo");

    dispatch(
      getAllPurchaseOrdersExportReport(
        {
          startRow: 0,
          endRow: Math.pow(10, 10),
          filterModel: {
            issue_date: {
              filter: parseInt(dateToUnix(startDate)),
              filterTo: parseInt(dateToUnix(endDate)),
              filterType: "number",
              type: "inRange",
            },
          },
          sortModel: [],
        },
        enqueueSnackbar
      )
    );
  }, [dispatch, enqueueSnackbar, getValues]);

  useEffect(() => {
    return () => dispatch(purchaseOrderActions.resetAllPurchaseOrdersExport());
  }, [dispatch]);

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">{t("purchase.order.index")}</Typography>
        <CustomizedButton
          title="นำออกข้อมูล"
          variant="contained"
          onClick={onBtnExport}
        />
      </Box>
      <CustomizedBox>
        <Typography ml={1} mb={2} fontWeight="bold">
          ตัวกรองแสดงผล
        </Typography>
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6}>
              <NewReportDateFilter
                t={t}
                control={control}
                setValue={setValue}
                getValues={getValues}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2} lg={2} alignSelf="center">
              <CustomizedButton
                title={t("button.submitFilter")}
                variant="contained"
                onClick={getFilteredPurchaseOrder}
                disabled={isLoading.allPurchaseOrder}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2} lg={2} alignSelf="center">
              <CustomizedButton
                title={t("button.resetFilter")}
                variant="outlined"
                onClick={onFilterReset}
                disabled={isLoading.allPurchaseOrder}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      </CustomizedBox>
      {allPurchaseOrdersExport && allPurchaseOrdersExport.length > 0 && (
        <PurchaseOrderTable
          gridRef={gridRef}
          rowData={allPurchaseOrdersExport}
          isReport
        />
      )}
    </>
  );
};

export default PurchaseOrderReport;
